import React, { useCallback } from 'react'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'

import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import PriceChangeViewV2 from 'components/Common/PriceChangeView/PriceChangeViewV2'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppDispatch } from 'hooks/reduxHooks'
import { updateBedbankPricingData } from 'actions/BedbankOfferActions'
import { sum } from 'lib/array/arrayUtils'

interface Props {
  offerId: string;
  checkIn: string;
  checkOut: string;
  rates: Array<{
    rate: App.BedbankRate;
    newPrice?: number;
    newPropertyFees?: number;
    rooms: Array<OfferPageRoom>;
  }>
}

function BedbankChangePriceModal(props: React.PropsWithChildren<Props>) {
  const {
    offerId,
    checkIn,
    checkOut,
    rates,
  } = props

  const { resolve } = useModalElementContext<Array<App.BedbankRate> | undefined>()
  const dispatch = useAppDispatch()

  const onAcceptNewPrice = useCallback(() => {
    const newRates = rates.map(({ rate, rooms, newPrice, newPropertyFees }) => {
      const resolvedPrice = newPrice ?? rate.totals.inclusive
      const resolvedPropertyFees = newPropertyFees ?? rate.totals.propertyFees ?? 0

      if (resolvedPrice === rate.totals.inclusive && resolvedPropertyFees === (rate.totals.propertyFees ?? 0)) {
        // Not all of the rates have necessarily changed
        return rate
      }

      dispatch(updateBedbankPricingData(
        offerId,
        rate.roomId,
        rate.id,
        rate.isFlightBundle,
        checkIn,
        checkOut,
        rooms,
        // LIMITATION: The price change error only tells us the total, not the room level changes
        // We simply estimate by dividing by the # of rooms
        {
          data: [{
            inclusive: resolvedPrice / rooms.length,
            propertyFees: resolvedPropertyFees / rooms.length,
          }],
        },
      ))

      const newRate: App.BedbankRate = {
        ...rate,
        totals: {
          ...rate.totals,
          inclusive: resolvedPrice,
          propertyFees: resolvedPropertyFees,
        },
      }

      return newRate
    })

    resolve(newRates)
  }, [rates, dispatch, offerId, checkIn, checkOut, resolve])

  const newDisplayPrice = sum(rates, ch => ch.newPrice ?? ch.rate.totals.inclusive)
  const oldDisplayPrice = sum(rates, ch => ch.rate.totals.inclusive)
  const newDisplayPropertyFees = sum(rates, ch => ch.newPropertyFees ?? ch.rate.totals.propertyFees ?? 0)
  const oldDisplayPropertyFees = sum(rates, ch => ch.rate.totals.propertyFees ?? 0)

  return (
    <ModalBase size="S">
      <ModalHeader
        title="Price Update"
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={24}>
            <BodyText as="p" variant="large">
              The price of your package has changed due to updated availability. Please review the new price and accept it to continue.
            </BodyText>
            <PriceChangeViewV2
              newPrice={newDisplayPrice}
              oldPrice={oldDisplayPrice}
              newPropertyFees={newDisplayPropertyFees}
              oldPropertyFees={oldDisplayPropertyFees}
            />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Accept updated price',
          onClick: onAcceptNewPrice,
        }}
        secondaryActionProps={{
          children: 'Return to offer',
        }}
      />
    </ModalBase>
  )
}

export default BedbankChangePriceModal
